'use client';

import {
  createContext,
  useMemo,
  useCallback,
  useState,
  useEffect,
  useLayoutEffect,
  useRef,
  useContext,
  PropsWithChildren,
} from 'react';

import { Types } from '@scorenco/core';

export const ScreenViewerContext = createContext<
  ScreenViewerContextProps | undefined
>(undefined);

export const useScreenViewerContext = (): ScreenViewerContextProps => {
  const context = useContext(ScreenViewerContext);
  if (!context) {
    throw new Error(
      'useScreenViewerContext must be inside an ScreenViewerProvider'
    );
  }
  return context;
};

export type ScreenViewerContextProps = {
  play: () => void;
  pause: () => void;
  paginate: (number: number) => void;
  autoPlay: boolean;
  isAnimating: boolean;
  current: Types.Clubhouse_Screen | undefined;
  page: number;
  screens: Types.Clubhouse_Screen[];
  duration: number;
};

type ScreenViewerProviderProps = PropsWithChildren<{
  screens: Types.Clubhouse_Screen[];
}>;

export const ScreenViewerProvider = ({
  screens,
  children,
}: ScreenViewerProviderProps) => {
  const screensCount = screens.length;
  const [page, setPage] = useState(0);
  const [isAnimating, setIsAnimating] = useState(true);
  const [autoPlay, setAutoPlay] = useState(true);
  const [duration] = useState(15000);

  const autoPlayRef = useRef(autoPlay);
  useLayoutEffect(() => {
    autoPlayRef.current = autoPlay;
  }, [autoPlay]);

  const changePage = useCallback((newPage: number) => {
    setIsAnimating(false);
    setPage(newPage);
  }, []);

  const paginate = useCallback(
    (newDirection: number) => {
      if (screensCount === 0) {
        return;
      }

      const newPage = (page + newDirection + screensCount) % screensCount;
      changePage(newPage);
    },
    [changePage, page, screensCount]
  );

  const play = useCallback(() => {
    setIsAnimating(true);
    setAutoPlay(true);
  }, []);

  const pause = useCallback(() => {
    setAutoPlay(false);
  }, []);

  const current = useMemo(() => screens[page], [screens, page]);

  useEffect(() => {
    const timeout = setTimeout(() => {
      setIsAnimating(autoPlayRef.current);
    }, 0);
    return () => clearTimeout(timeout);
  }, [page]);

  const value = useMemo(
    () => ({
      current,
      play,
      pause,
      paginate,
      autoPlay,
      isAnimating,
      page,
      screens,
      duration,
    }),
    [
      current,
      page,
      play,
      duration,
      pause,
      paginate,
      screens,
      autoPlay,
      isAnimating,
    ]
  );

  return (
    <ScreenViewerContext.Provider value={value}>
      {children}
    </ScreenViewerContext.Provider>
  );
};
